import { ChangeEvent, useState } from "react";
import { undoInvoices } from "../../api/services/invoiceService";
import { InvoiceExportDetail, InvoiceExportDetailGrouping } from "../../api/models/invoiceExportDetail";
import { useAsync } from "react-async";
import { useHistory, useLocation } from "react-router-dom";
import InvoiceExportDetailResponse from "../../api/responses/invoiceExportDetailResponse";
import Layout from "../../components/layout";
import { InvoiceExportRequestDistrict } from "../../api/requests/invoiceExportRequest";

type ViewState = {
    exportedInvoices: InvoiceExportDetailGrouping;
};

const getExportedInvoicesToUndo = (invoices: InvoiceExportDetail[] | null) => {
    return invoices?.filter(x => x.isMarkedForExportReversal) ?? [];
}

const InvoiceExportsEdit = () => {
    const history = useHistory();
    const { state } = useLocation<ViewState>();
    const firstInvoice = state.exportedInvoices.exportGroup[0];
    const [exportedInvoices, setExportedInvoices] = useState<InvoiceExportDetail[]>(state.exportedInvoices.exportGroup);
    const [undoExportsEnabled, setUndoExportsEnabled] = useState<boolean>(false);

    const handleMarkForExportReversal = (e: ChangeEvent<HTMLInputElement>, invoice: InvoiceExportDetail) => {
        const exportedInvoicesDeepClone: InvoiceExportDetail[] = JSON.parse(JSON.stringify(exportedInvoices));
        const invoiceIndex = exportedInvoicesDeepClone.findIndex(i => i.invoiceNumber === invoice.invoiceNumber);

        invoice.isMarkedForExportReversal = e?.target.checked;
        exportedInvoicesDeepClone[invoiceIndex] = invoice;

        setExportedInvoices(exportedInvoicesDeepClone);

        const exportsToUndo = getExportedInvoicesToUndo(exportedInvoicesDeepClone).length > 0;
        setUndoExportsEnabled(exportsToUndo);
    }

    const selectAll = (isSelected: boolean) => {
        const exportedInvoicesDeepClone: InvoiceExportDetail[] = JSON.parse(JSON.stringify(exportedInvoices));

        exportedInvoicesDeepClone.map(i => setTimeout(() => i.isMarkedForExportReversal = isSelected, 0));

        setExportedInvoices(exportedInvoicesDeepClone);

        const exportsToUndo = getExportedInvoicesToUndo(exportedInvoicesDeepClone).length > 0;
        setUndoExportsEnabled(exportsToUndo);
    }

    const navigateToExportsSummary = (exportedInvoicesGroupList: InvoiceExportDetailGrouping[]) => {
        history.push(`/accounting/invoice/exports`, { exportedInvoicesGroupList });
    }

    const cancel = () => {
        history.goBack();
    }

    const undoExports = () => {
        const invoiceDistrictIdsToUndo = exportedInvoices
            ?.filter(d => d.isMarkedForExportReversal)
            .map((d, i) => d.districtId) ?? [];

        const invoiceDistrictsToExport: InvoiceExportRequestDistrict[] = [];

        exportedInvoices?.filter(d => d.isMarkedForExportReversal).map((d, i) => {
            invoiceDistrictsToExport.push({
                districtId: d.districtId,
                programId: d.programId,
            });
        })

        undoExportsAsync.run({
            year: firstInvoice.year,
            month: firstInvoice.month,
            districtIds: invoiceDistrictIdsToUndo,
            districts: invoiceDistrictsToExport,
            undoExport: true,
        })
    }

    const undoExportsAsync = useAsync({
        deferFn: (args: any[]) => undoInvoices(args[0]),
        onResolve: (response: InvoiceExportDetailResponse | null) => {
            const invoiceExportDetailGrouping: InvoiceExportDetailGrouping[] =
                response?.data?.exportedInvoicesByDate ?? [] as InvoiceExportDetailGrouping[];

            navigateToExportsSummary(invoiceExportDetailGrouping);
        },
        onReject: (error) => {
            window.alert(error);
        }
    });

    return (
        <Layout isLoading={undoExportsAsync.isLoading}>
            <div className='px-4 py-3 mt-1 bg-white rounded-lg shadow'>
                <div className="mb-5">
                    <button className='bg-ptsblue-normal hover:bg-ptsblue-dark' onClick={cancel}>
                        Cancel
                    </button>
                    <span className="mx-2"></span>
                    <button className='bg-ptsgreen-normal hover:bg-ptsgreen-dark'
                        onClick={undoExports}
                        disabled={!undoExportsEnabled}
                    >
                        Undo Invoice Export(s)
                    </button>
                </div>
                <h3 className="my-4">Select the invoice exports to be reversed.</h3>
                <p className="my-4">
                    Select:&nbsp;&nbsp;
                    <span className='cursor-pointer text-ptsblue-normal hover:underline' onClick={(e) => selectAll(true)}>
                        All
                    </span>
                    <span> | </span>
                    <span className='cursor-pointer text-ptsblue-normal hover:underline' onClick={(e) => selectAll(false)}>
                        None
                    </span>
                </p>
                <table className='w-full border-collapse rounded-lg border-spacing-x-4'>
                    <thead className="font-bold align-bottom">
                        <tr className="text-left">
                            <th className="pl-2 mx-2">Select</th>
                            <th className="pl-2 mx-2">District Name</th>
                            <th className="mx-2">Invoice Number</th>
                        </tr>
                    </thead>
                    <tbody className="rounded-lg">
                        {exportedInvoices?.map((ei, idx) => (
                            <tr key={`exportedInvoice_${idx}`}
                                id={`exported-invoice-district-${ei.districtId}`}
                                className="h-16 rounded-lg odd:bg-gray-100"
                            >
                                <td className="pl-2 mx-2">
                                    <input
                                        id={`mark-for-reversal_${idx}`}
                                        name={`mark-for-reversal_${idx}`}
                                        key={`mark-for-reversal_${idx}`}
                                        title='Mark to undo export'
                                        type='checkbox'
                                        className='mr-4 primary-checkbox'
                                        disabled={false}
                                        checked={ei.isMarkedForExportReversal}
                                        onChange={(e) => handleMarkForExportReversal(e, ei)}
                                    />
                                </td>
                                <td className="pl-2 mx-2">{ei.districtName}</td>
                                <td className="mx-2">{ei.invoiceNumber}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Layout>
    );
};

export default InvoiceExportsEdit;